<script setup>
import { ref, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia'
import { useFilterStore } from '@/stores/modules/filter';

const filterStore = useFilterStore();
const {
  startDate,
  endDate,
  brandCode,
  familyCode,
  indicationCode,
  bu,
  subBu,
  rsd,
  rsm,
  dsm,
  mr
} = storeToRefs(filterStore);
import dayjs from 'dayjs';
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { getStaffStructureTree, getBrandList } from '@/api/sidebar';

const isCollapsed = ref(true);
const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value;
};

const dateRange = ref([filterStore.startDate + '', filterStore.endDate + '']);
const brandOptions = ref([]);
const getBrandOptions = () => {
  getBrandList({
    startDate: startDate.value,
    endDate: endDate.value,
    brandCode: brandCode.value,
    familyCode: familyCode.value,
    indicationCode: indicationCode.value,
    bu: bu.value,
    subBu: subBu.value,
    rsd: rsd.value,
    rsm: rsm.value,
    dsm: dsm.value,
    mr: mr.value
  }).then(res => {
    // console.log(res)
    brandOptions.value = res.data?.children;
  })
}

const sortChildren = (children) => {
  if (!Array.isArray(children) || children.length === 0) return;
  
  // 排序当前层级
  children.sort((a, b) => {
    return a.label.localeCompare(b.label, 'zh-CN', {
      sensitivity: 'base',
      numeric: true
    });
  });
  
  // 递归处理下一层级
  children.forEach(item => {
    if (item.children && Array.isArray(item.children)) {
      sortChildren(item.children);
    }
  });
};

const structureOptions = ref([]);
const getStaffOptions = () => {
  getStaffStructureTree({
    startDate: startDate.value,
    endDate: endDate.value,
    brandCode: brandCode.value,
    familyCode: familyCode.value,
    indicationCode: indicationCode.value,
    bu: bu.value,
    subBu: subBu.value,
    rsd: rsd.value,
    rsm: rsm.value,
    dsm: dsm.value,
    mr: mr.value
  }).then(res => {
    // console.log(res)
    if (res.data && Array.isArray(res.data.children)) {
      // 复制一份数据避免修改原始响应
      const sortedChildren = [...res.data.children];
      //排序
      sortChildren(sortedChildren);
      structureOptions.value = sortedChildren;
    } else {
      // 处理空数据情况
      structureOptions.value = [];
    }
  })
}

// 限制月份选择范围为当前月及之前26个月
const disabledDate = (time) => {
  const now = dayjs();
  const twentySixMonthsAgo = now.subtract(13, 'month');
  return time > now.endOf('month') || time < twentySixMonthsAgo.startOf('month');
};

// 搜索处理
const changeDateRange = () => {
  startDate.value = parseInt(dateRange.value[0]);
  endDate.value = parseInt(dateRange.value[1]);
  // 调用接口
  getStaffOptions();
  getBrandOptions();
};

const screenWidth = ref(window.screen.width);
const isSmallScreen = computed(() => {
  return screenWidth.value <= 1550;
});

onMounted(() => {
  getStaffOptions();
  getBrandOptions();
});

const cascaderProps = {
  multiple: false,
  checkStrictly: true,
  // 后续优化成懒加载
  lazy: false,
  lazyLoad(node, resolve) {
    // console.log("lazyLoad",node)
    resolve(node.children)
  },
}

const structureChange = (val) => {
  console.log("structureChange", val)
  filterStore.resetStructure();
  if (val && val.length > 0) {
    val.forEach((item, index) => {
      switch (index) {
        case 0:
          bu.value = item;
          break;
        case 1:
          subBu.value = item;
          break;
        case 2:
          rsd.value = item;
          break;
        case 3:
          rsm.value = item;
          break;
        case 4:
          dsm.value = item;
          break;
        case 5:
          mr.value = item;
          break;
      }
    })
  }
  getBrandOptions();
}

const brandChange = (val) => {
  console.log("brandChange", val)
  filterStore.resetBrand();
  if (val && val.length > 0) {
    val.forEach((item, index) => {
      switch (index) {
        case 0:
          brandCode.value = item;
          break;
        case 1:
          familyCode.value = item;
          break;
        case 2:
          indicationCode.value = item;
          break;
      }
    })
  }
  getStaffOptions();
}
const structureValue = ref([]);
const brandValue = ref([]);

const resetFilterData = () => {
  filterStore.resetFilter();
  // 重置表单显示值
  dateRange.value = [filterStore.startDate + '', filterStore.endDate + ''];
  brandValue.value = [];
  structureValue.value = [];
  getStaffOptions();
  getBrandOptions();
}
</script>

<template>
  <div class="sidebar" :class="{ 'collapsed': isCollapsed }">
    <el-card shadow="always">
      <div class="sidebar__toggle" @click="toggleSidebar">
        <svg class="sidebar__toggle-icon icon">
          <use xlink:href="#icon-zhankai"></use>
        </svg>
      </div>
      <el-form class="search-form" label-position="top">
        <!-- 月份区间选择器 -->
        <el-config-provider :locale="zhCn">
          <el-form-item label="时间">
            <el-date-picker v-model="dateRange" type="monthrange" range-separator="至" start-placeholder="开始月份"
              end-placeholder="结束月份" :disabled-date="disabledDate" style="width: 100%;" @change="changeDateRange"
              format="YYYY-MM" value-format="YYYYMM" popper-class="custom-cascader" />
          </el-form-item>
        </el-config-provider>

        <!-- 产品选择器 -->
        <el-form-item label="关联产品">
          <el-cascader :options="brandOptions" :props="cascaderProps" placeholder="ALL" style="width: 100%;" clearable
            filterable @change="brandChange" v-model="brandValue">
            <!-- <template #default="{ node, data }">
              <span v-if="data.level !== 'ALL' && data.level !== 'BU' && data.level !== 'SUB_BU'">{{
                data.level+'：'}}</span>
              <span>{{ data.label }}</span>
            </template> -->
          </el-cascader>
          <!-- <el-select v-model="brandCode" @change="brandChange" style="width: 100%;" placeholder="ALL" clearable>
            <el-option v-for="item in brandOptions" :key="item.brandCode" :label="item.brandName"
              :value="item.brandCode" />
          </el-select> -->
        </el-form-item>

        <!-- 员工架构选择器 -->
        <el-form-item label="员工架构">
          <el-cascader :popper-class="isSmallScreen ? 'emp-select-smallscreen' : 'emp-select'"
            :options="structureOptions" :props="cascaderProps" placeholder="ALL" style="width: 100%;font-size: 10px"
            clearable filterable @change="structureChange" v-model="structureValue">
            <template #default="{ node, data }">
              <span v-if="data.level !== 'ALL' && data.level !== 'BU' && data.level !== 'SUB_BU'">{{
                data.level + '：'
                }}</span>
              <span>{{ data.label }}</span>
              <!-- <span v-if="!node.isLeaf"> ({{ data.children.length }}) </span> -->
            </template>
          </el-cascader>
        </el-form-item>
      </el-form>
      <div class="search-form__footer">
        <el-button type="primary" @click="resetFilterData" style="width: 100%;">重置</el-button>
      </div>
    </el-card>
  </div>
</template>

<style scoped lang="scss">
.search-form {
  padding: 16px;
  width: 100%;
}

.el-form-item {
  margin-bottom: 16px;
  width: 100%;
}

.sidebar {
  display: flex;
  flex-direction: column;
  width: 20rem;
  position: relative;
  transition: width 0.3s ease;

  &.collapsed {
    width: 36px;
  }

  .el-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    border-radius: 0;

    ::v-deep(.el-card__body) {
      flex: 1;
      padding: 0;
    }

    ::v-deep(.el-card__footer) {
      flex-shrink: 0;
    }

    .search-form__footer {
      // 固定到最下方
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 16px;
    }

    .sidebar__toggle {
      cursor: pointer;
      padding: 8px;
      font-size: 18px;
      position: relative;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      flex-direction: row-reverse;
    }

    .sidebar__toggle-icon {
      height: 18px;
      width: 18px;
      display: inline-block;
      transition: transform 0.3s ease;
      transform: rotate(180deg);
      color: var(--color-text);
    }


  }


  &.collapsed .search-form,
  &.collapsed .search-form__footer {
    display: none;
    padding: 0px;
  }

  &.collapsed .sidebar__toggle-icon {
    transform: rotate(0deg);
  }


}

:global(.emp-select .el-cascader-panel) {
  --el-cascader-menu-font-size: 14px !important;
}

/* 小屏幕样式 */
:global(.emp-select-smallscreen .el-cascader-panel) {
  --el-cascader-menu-font-size: 10px !important;
}



// .emp-select ::v-deep .el-cascader__dropdown .el-cascader-panel {
//   --el-cascader-menu-font-size: 10px !important;
// }
// .el-button:hover {
//   color: var(--az-color-purple-100);
// }</style>
